/**
 * API module for federated user invitation operations.
 * Handles HTTP requests and error mapping for invitation management.
 */

import { getConfig } from '../config'

/**
 * Fetches invitation status for a federated user by their identity.
 *
 * @param {string} connection - Federated connection name (e.g., "google-oauth2")
 * @param {string} externalId - External user ID from identity provider
 * @returns {Promise<{data: Object|null, error: string|null}>} Result object with data or error
 *
 * @example
 * const result = await getInvitationByIdentity('google-oauth2', '123456')
 * if (result.error) {
 *   console.error(result.error)
 * } else {
 *   console.log(result.data)
 * }
 */
export async function getInvitationByIdentity(connection, externalId) {
  try {
    const params = new URLSearchParams({
      connectionName: connection,
      externalId: externalId,
    })
    const url = `${getUserServiceUrl()}/api/invitations/federated-users/by-identity?${params}`

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (response.ok) {
      const data = await response.json()
      return { data, error: null }
    }

    if (response.status === 404) {
      // No invitation found - return null data without error
      return { data: null, error: null }
    }

    // Server error or other issue
    console.error('Error fetching invitation:', response.status)
    return {
      data: null,
      error: 'Unable to verify invitation status. Please try again or contact your support team for assistance.',
    }
  } catch (error) {
    console.error('Error fetching invitation data:', error)
    return {
      data: null,
      error: 'Unable to verify invitation status. Please try again or contact your support team for assistance.',
    }
  }
}

/**
 * Resends invitation email to a federated user.
 *
 * @param {string} connection - Federated connection name (e.g., "google-oauth2")
 * @param {string} externalId - External user ID from identity provider
 * @returns {Promise<{data: Object|null, error: string|null}>} Result object with data or error
 *
 * @example
 * const result = await resendInvitation('google-oauth2', '123456')
 * if (result.error) {
 *   console.error(result.error)
 * } else {
 *   console.log('Email sent successfully')
 * }
 */
export async function resendInvitation(connection, externalId) {
  try {
    const url = `${getUserServiceUrl()}/api/invitations/federated-users/resend`
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        connectionName: connection,
        externalId: externalId,
      }),
    })

    if (response.ok) {
      const data = await response.json()
      return { data, error: null }
    }

    if (response.status === 400) {
      return {
        data: null,
        error: 'Invalid request. Please contact support.',
      }
    }

    if (response.status >= 500) {
      return {
        data: null,
        error: 'Server error. Please try again later.',
      }
    }

    return {
      data: null,
      error: 'Unable to send email. Please try again.',
    }
  } catch (error) {
    console.error('Error resending email:', error)
    return {
      data: null,
      error: 'Unable to send email. Please try again.',
    }
  }
}

const getUserServiceUrl = () => {
  const { userServiceUrl } = getConfig()
  return userServiceUrl
}
